/* eslint-disable camelcase */
import styled from '@emotion/styled';
import React, { useEffect, useMemo } from 'react';
import { Row, Col, Button, Select, Input, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { unstable_batchedUpdates } from 'react-dom';
import { useTranslate } from '@tolgee/react';
import { debounce } from 'lodash-es';
import RegionSelector from 'views/Aide/components/RegionSelector';
import PlatformSelector from 'views/Aide/components/PlatformSelector';
import { useSetGlobalQuery } from 'hooks/business/useGlobalQuery';
import useRegionList from '../hooks/useRegionList';
import useStoreList from '../hooks/useStoreList';
import useReasons from '../hooks/useReasons';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { BaseOptionType } from 'antd/lib/select';

const Container = styled.nav`
  background-color: #ffffff;
  padding: 0;
  position: relative;
  flex: none;

  .other-row {
    height: 56px;
    align-items: center;
    padding: 0 16px;
    .loading-box {
      display: flex;
      justify-content: center;
    }
    .select-group {
      display: flex;
      gap: 24px;
      height: 100%;
      align-items: center;
      > .ant-select {
        width: 160px;
      }
      .ant-input-group-compact {
        display: flex;
        flex: 1;
        .ant-select {
          width: 100px;
          flex: none;
        }
        .ant-input {
          width: 180px;
        }
      }
    }
    .button-group {
      display: flex;
      gap: 16px;
    }
  }
`;
export type OtherCondition = {
  store?: number;
  reason?: string;
  groupInput: {
    type: 'buyerNick' | 'operatorBy' | 'notice';
    value: string;
  };
};

interface Props {
  platform?: General.PlatformUpperCase;
  other: OtherCondition;
  region: General.UpperCaseRegion[];
  onPlatformChange: (value?: General.PlatformUpperCase) => void;
  onRegionChange: (value: General.UpperCaseRegion[]) => void;

  onOtherChange: (value: OtherCondition) => void;
  onSearch: () => void;
}

const FilterHeader = React.memo((props: Props) => {
  const { platform, region, other, onOtherChange, onPlatformChange, onRegionChange, onSearch } =
    props;
  const { t } = useTranslate();
  const { regions, platforms } = useRegionList();
  const {
    loadMore,
    loading,
    list: storeList,
    hasMore,
    onSearch: onSearchStore,
  } = useStoreList(region, platform);
  const setGlobalQuery = useSetGlobalQuery();

  const reasons = useReasons(true);

  const disabled = !other.store && !other.reason && !other.groupInput.value;

  const { configMap } = useStoreNameConfig();

  const showPlatforms = useMemo(() => {
    return platforms || [];
  }, [platforms]);
  useEffect(() => {
    if (showPlatforms.length && !platform) {
      onPlatformChange(showPlatforms[0]);
    }
  }, [showPlatforms, onPlatformChange, platform]);
  if (!platform) {
    return null;
  }

  const platformSelector = (
    <PlatformSelector
      platformList={showPlatforms}
      activePlatform={platform}
      onActivePlatformChange={(platform) => {
        unstable_batchedUpdates(() => {
          onPlatformChange(platform);
          onRegionChange([]);
          setGlobalQuery({
            platform,
          });
        });
      }}
    />
  );

  if (platform === 'ALIEXPRESS') {
    return <Container>{platformSelector}</Container>;
  }

  return (
    <Container>
      {platformSelector}
      <RegionSelector
        regionList={[...regions[platform], 'ALL']}
        activeRegion={region[0] || 'ALL'}
        onActiveRegionChange={(region) => {
          onRegionChange(region === 'ALL' ? [] : [region]);
          setGlobalQuery({
            platform,
            region: region === 'ALL' ? undefined : region,
          });
        }}
      />
      <Row justify="space-between" className="other-row">
        <Col className="select-group">
          <Select
            dropdownMatchSelectWidth={false}
            title={other.store ? undefined : (t('ViewBlockList.选择店铺') as string)}
            placeholder={t('ViewBlockList.选择店铺')}
            value={other.store}
            showSearch
            allowClear
            onSearch={debounce(onSearchStore, 150)}
            onChange={(value) => {
              onOtherChange({
                ...other,
                store: value,
              });
            }}
            onPopupScroll={(e) => {
              e.persist();
              const target = e.target as any;
              if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
                loadMore();
              }
            }}
            options={storeList
              .map<BaseOptionType>(({ storeId, storeName, alias, externalType, region }) => {
                return {
                  value: storeId,
                  label:
                    externalType === 'WA_BIZ'
                      ? storeName
                      : configMap?.[externalType]?.[region]
                      ? alias || storeName
                      : storeName,
                };
              })
              .concat(
                hasMore && loading
                  ? [
                      {
                        value: 'loading',
                        label: (
                          <div className="loading-box">
                            <LoadingOutlined />
                          </div>
                        ),
                        disabled: true,
                      },
                    ]
                  : [],
              )}
          />
          <Select
            title={t('ViewBlockList.选择拉黑原因')}
            placeholder={t('ViewBlockList.选择拉黑原因')}
            value={other.reason}
            allowClear
            onChange={(val) => {
              onOtherChange({
                ...other,
                reason: val,
              });
            }}
          >
            {reasons.map((x) => (
              <Select.Option value={x} key={x}>
                {x}
              </Select.Option>
            ))}
          </Select>
          <Input.Group compact>
            <Select
              dropdownMatchSelectWidth={false}
              onChange={(value: any) => {
                onOtherChange({
                  ...other,
                  groupInput: {
                    type: value,
                    value: '',
                  },
                });
              }}
              value={other.groupInput?.type}
              options={[
                { value: 'buyerNick', label: t('ViewBlockList.filter.other.buyerNick') },
                { value: 'operatorBy', label: t('ViewBlockList.filter.other.operatorBy') },
                { value: 'notice', label: t('ViewBlockList.filter.other.notice') },
              ]}
            />
            <Input
              value={other.groupInput?.value}
              title={t(`ViewBlockList.filter.other.${other.groupInput.type}.placeholder`)}
              placeholder={t(`ViewBlockList.filter.other.${other.groupInput.type}.placeholder`)}
              onChange={(e) => {
                onOtherChange({
                  ...other,
                  groupInput: {
                    type: other.groupInput.type,
                    value: e.target.value,
                  },
                });
              }}
            />
          </Input.Group>
          <Button type="primary" onClick={onSearch} disabled={disabled}>
            {t('ViewBlockList.filter.search')}
          </Button>
          <Button
            disabled={disabled}
            onClick={() => {
              onOtherChange({
                groupInput: {
                  type: other.groupInput.type,
                  value: '',
                },
              });
              onSearch();
              message.success(t('ViewBlockList.重置成功'));
            }}
          >
            {t('ViewBlockList.filter.reset')}
          </Button>
        </Col>
      </Row>
    </Container>
  );
});

export default FilterHeader;
